import { apiRequest } from '@common/api/utils';
import {
  Form, FormFlow, Modal, Select, SingularOTPAutofill,
} from '@common/vanilla_ui/general';
import { FlowTypes } from '@common/vanilla_ui/general/formFlow';
import { welcomeModalEvents } from '@common/vanilla_ui/general/welcomeModal';
import { OTP_TIMEOUT } from '@common/vanilla_ui/forms/otpForm';
import ValidateSelect from
  '@common/vanilla_ui/forms/validators/selectValidator';
import { modalEvents } from '@common/vanilla_ui/general/modal';
import gtmTracking from '@common/vanilla_ui/tracking/gtm';
import { getPlatform } from '@common/vanilla_ui/tracking/attribution';
import { isPresent } from '@common/utils/type';
import BookCounselling from
  '@programs_landing/components/undergrad/bookCounselling';
import DownloadContent from
  '@programs_landing/components/undergrad/downloadContent';
import DelaconWidget from
  '@programs_landing/components/undergrad/delaconWidget';
import WaitlistModalActions from
  '@programs_landing/components/undergrad/waitlistModalActions';
import PrepkitDownload from
  '@programs_landing/components/undergrad/prepkitDownload';
import NsetPreparationVideo
  from '@programs_landing/components/undergrad/nsetPreparationVideo';
import placementDownload
  from '@programs_landing/components/mba/placementDownload';
import { initializeCustomSelect } from './customSelect';

const defaultErrorMessage = 'We are unable to process your request right now,'
  + ' please try again in sometime.';
const invalidPhoneErrorMessage = 'Please enter a valid mobile number.';
const waitlistFormHandlers = {
  'waitlist-register-otp': '_waitlistRegisterOtpHandler',
  'waitlist-login-mobile': '_waitlistLoginMobileHandler',
  'waitlist-login-otp': '_waitlistLoginOtpHandler',
  'waitlist-login-otp-autofill': '_waitlistLoginOtpAutofillHandler',
  'waitlist-form': '_waitlistFormHandler',
  'waitlist-form-status': '_waitlistFormStatus',
  'waitlist-account-update': '_waitlistAccountUpdateHandler',
};
const parentFormEl = document.querySelector('.form-flow__modes');
const activeWaitlistMode = document.querySelector(
  '#waitlist-modal .form-flow__mode--active',
);
const waitlistModalEl = document.getElementById('waitlist-modal');
const WHATSAPP_CONSENT_DEFAULT = 'whatsapp_consent_yes';
let countdownInterval;

/**
 * The flow for integrating the login/signup modals:
 * *For Predefined Handlers:*
 *  1) Initialise new Auth Flow object using (Default Mode will be register
 *      and all handlers will be initialised automatically):
 *        new AuthFlow('auth-modal');
 *         ** If you have to make default mode to be login-mobile:
 *           new AuthFlow('auth-modal', 'login-mobile');
 * *For Custom Handlers:*
 *  1) Initialise new Auth Flow object using (Default Mode will be register):
 *      const auth = new AuthFlow('auth-modal', 'register', false);
 *        // false to stop auto initialisation of handlers
 *        // used to set custom handlers
 *      ** If you have to make default mode to be login-mobile:
 *         const auth = new AuthFlow('auth-modal', 'login-mobile', false);
 *  2) First set your custom login/signup handlers by using:
 *      auth.setCustomFormHandler(
 *        'register', registerHandler       //for custom register handler
 *      );
 *      auth.setCustomFormHandler(
 *        'login-mobile', loginMobileHandler  //for custom login-mobile handler
 *      );
 *  3) Once all the custom handlers are set then initialise all the handlers:
 *      auth.authFlowInitialize();
 */

const trackSubmitStatus = (formId, status, message = '') => {
  const formTriggerSource = waitlistModalEl?.getAttribute('data-form-source');

  window?.GTMtracker.pushEvent({
    event: 'form_submit_status',
    data: {
      form_id: formId,
      status,
      message,
      extra_info: {
        form_source: formTriggerSource,
      },
    },
  });
};

const handleQueryParams = (formEl) => {
  if (!formEl) return;

  const queryString = window.location.search;
  const queryParams = new URLSearchParams(queryString);
  if (queryParams.get('open-waitlist-form') === 'true'
    && formEl.dataset.waitlistFormSubmit === 'true') {
    Modal.open('waitlist-modal', true);
  }
};

const handleForceOpen = () => {
  const queryString = window.location.search;
  const queryParams = new URLSearchParams(queryString);
  if (queryParams.get('force-form-open') === 'true' && activeWaitlistMode) {
    Modal.open('waitlist-modal', true);
  }
};

const handleAccountUpdateFormOpen = (waitlistFlow) => {
  if (!waitlistFlow._loggedIn) {
    return;
  }

  let accountUpdateModeActive = false;
  if (sessionStorage.getItem('truecaller-signup')) {
    sessionStorage.removeItem('truecaller-signup');

    const activeWaitlistModes = document.querySelectorAll(
      '#waitlist-modal .form-flow__mode--active',
    );

    activeWaitlistModes.forEach(waitlistMode => {
      if (waitlistMode?.dataset.mode === 'waitlist-account-update') {
        waitlistFlow.switchMode('waitlist-account-update');
        accountUpdateModeActive = true;
      }
    });
  }
  if (!accountUpdateModeActive) {
    waitlistFlow.switchMode('waitlist-form');
  }
};

const handleResendOtpEls = (resendEleClass) => {
  const resendOtpEls = document.querySelectorAll(`.${resendEleClass}`);

  resendOtpEls?.forEach(ele => {
    ele.addEventListener('click', () => {
      const recaptchaTriggerEle = document.querySelector(
        '.trigger-recaptcha',
      );
      recaptchaTriggerEle?.setAttribute(
        'data-otp-channel',
        ele.getAttribute('data-otp-channel'),
      );
      recaptchaTriggerEle?.click();
    });
  });
};

const handleResendOTP = () => {
  Modal.close('waitlist-exit-intent-modal');
  const resendOtpCounter = document.querySelector(
    '.waitlist-form__resend-otp-counter',
  );
  if (!resendOtpCounter) return;

  const resendChannelContainer = document.querySelector(
    '.waitlist-form__resend-channel-wrapper',
  );
  resendChannelContainer?.classList.add('hidden');

  clearInterval(countdownInterval);
  resendOtpCounter.classList.remove('hidden');
  const engagementStripContainer = document.querySelector(
    '.waitlist-form__otp-strip-wrapper',
  );
  engagementStripContainer?.classList.remove('hidden');

  let timeLeft = OTP_TIMEOUT;
  countdownInterval = setInterval(() => {
    timeLeft -= 1;
    if (timeLeft === 0) {
      clearInterval(countdownInterval);
      resendOtpCounter.classList.add('hidden');
      engagementStripContainer?.classList.add('hidden');
      handleResendOtpEls('waitlist-form__resend-otp-channel');
      resendChannelContainer?.classList.remove('hidden');
      timeLeft = OTP_TIMEOUT;
    } else {
      resendOtpCounter.innerHTML = `Resend OTP in ${timeLeft}s`;
    }
  }, 1000);
};

const showError = (errorEl, formInputEl) => {
  errorEl.classList.remove('form-field__error--hidden');
  formInputEl.classList.add('waitlist-form__join-text-input--invalid');
};

const hideError = (errorEl, formInputEl) => {
  formInputEl.classList.remove('waitlist-form__join-text-input--invalid');
  errorEl.classList.add('form-field__error--hidden');
};

const errorTypeMessage = {
  text: 'Cannot be empty',
  email: 'Enter a valid email id.(Ex: example@test.com)',
  tel: 'Please enter a valid mobile number',
};

const handleFlashError = (error, formId) => {
  trackSubmitStatus(formId, false, error);
  window.location.reload();
};

const postPhoneVerificationActions = (userDetails) => {
  sessionStorage.setItem('sstRegistration', true);
  const url = new URL(window.location.href);
  const urlParams = new URLSearchParams(url.search);
  // adding the required params in the url
  urlParams.delete('force-form-open');
  urlParams.set('open-waitlist-form', true);

  const successAction = parentFormEl?.getAttribute('data-success-action');
  const successView = parentFormEl?.getAttribute('data-success-view');

  if (successAction === 'download_brochure') {
    urlParams.set('download-brochure', true);
  } else if (successAction === 'book_counselling') {
    urlParams.set('book-counselling', true);
  } else if (successAction === 'placement_download') {
    urlParams.set('placement_download', true);
  } else if (successAction === 'prepkit_download') {
    urlParams.set('prepkit-download', true);
  } else if (successAction === 'call_us_directly') {
    urlParams.set('call-us-directly', true);
  } else if (successAction === 'download_curriculum') {
    urlParams.set('download-curriculum', true);
  } else if (successAction === 'download_newsletter') {
    urlParams.set('download-newsletter', true);
  } else if (successAction === 'nset_preparation_video_button') {
    urlParams.set('nset-preparation-video-button', true);
  } else if (successAction === 'nset_preparation_video_thumbnail') {
    urlParams.set('nset-preparation-video-thumbnail', true);
  } else if (successAction === 'internship_report_download') {
    urlParams.set('internship-report-download', true);
  }

  if (successView === 'campus-life') {
    urlParams.set('campus-life-page', true);
  }
  window.GTMtracker?.trackEvent('loginWithPhoneFormatter', {
    userId: `${userDetails.user_id}`,
    email: userDetails.email,
    newUser: userDetails.is_new_user,
    phoneNumber: userDetails.phone_number,
    whatsappConsent: userDetails.whatsapp_consent,
    analyticsIdentifier: userDetails.analytics_identifier,
  });
  trackSubmitStatus('waitlist-login-otp-form', true);
  // sending back the modified url
  url.search = urlParams.toString();
  window.location.href = url.toString();
};

const handleExitIntentCloseBtn = () => {
  const closeBtn = document.getElementById('waitlist-exit-intent-close-btn');
  closeBtn?.addEventListener('click', () => {
    Modal.close('waitlist-modal');
  });
};

const handleChatbotWidget = () => {
  modalEvents.on('trigger:opened', (modalId) => {
    if (modalId === 'waitlist-exit-intent-modal') {
      const chatbotWidget = document.querySelector('.chatbot--elevate');
      chatbotWidget?.classList.add('chatbot--hide');
    }
  });

  modalEvents.on('trigger:dismissed', (modalId) => {
    if (modalId === 'waitlist-exit-intent-modal') {
      const chatbotWidget = document.querySelector('.chatbot--elevate');
      chatbotWidget?.classList.remove('chatbot--hide');
    }
  });
};

class WaitlistFlow extends FormFlow {
  constructor(id, waitTime = 3000, mode = 'register', autoInitialise = true) {
    const formType = FlowTypes.modal;
    super('waitlist', id, formType, mode);
    this._waitTime = waitTime;
    const formFlowEl = document.querySelector('#waitlist-modal');
    this._loggedIn = formFlowEl?.getAttribute('data-logged-in') === 'true';
    this._setFormHandlers();
    if (autoInitialise) this.waitlistFlowInitialize();
    handleAccountUpdateFormOpen(this);
    handleForceOpen();
    handleChatbotWidget();
    this._whatsappConsent = WHATSAPP_CONSENT_DEFAULT;
  }

  setCustomFormHandler(mode, handler) {
    this.setFormHandler(mode, handler);
  }

  _commonFlow = (gradYear, gtmAttribute) => {
    window.GTMtracker?.pushEvent({
      event: 'gtm_custom_click',
      data: {
        click_text: gradYear,
        click_type: 'UG Waitlist Twelfth Year',
      },
    });
    if (gradYear === 'Others') {
      trackSubmitStatus(
        gtmAttribute,
        false,
        'Applicant not eligible',
      );
    } else {
      trackSubmitStatus(gtmAttribute, true);
    }
  };

  _applicationProcessFlow = () => {
    window.location.replace('/school-of-technology/application');
  }

  _nsetPreparationFlow = () => {
    NsetPreparationVideo.nsetPreparationSuccessFlow();
  }

  _downloadBrochureFlow = () => {
    DownloadContent.downloadBrochureSuccessFlow();
  }

  _downloadCurriculumFlow = () => {
    DownloadContent.downloadCurriculumSuccessFlow();
  }

  _downloadNewsletterFlow = () => {
    DownloadContent.downloadNewsletterSuccessFlow();
  }

  _downloadPlacementFlow = () => {
    placementDownload.placementRecordDownloadSuccessFlow();
  }

  _bookCounsellingFlow = (gradYear) => {
    if (gradYear !== 'Others') {
      BookCounselling.callbackRequest();
      Modal.open('counselling-modal');
      BookCounselling.showCallbackModal();
    }
  }

  _prepkitDownloadFlow = () => {
    PrepkitDownload.prepkitDownloadSuccessFlow();
  }

  _callUsDirectlyFlow = () => {
    Modal.open('delacon-number-modal');
    DelaconWidget.showCallUs();
  }

  _downloadInternshipReportFlow = () => {
    DownloadContent.downloadInternshipReportSuccessFlow();
  }

  /**
   * Called separately as to provide flexibility for declaring custom handlers
   */
  waitlistFlowInitialize() {
    this.initialize();
  }
  /* Private */

  _setFormHandlers() {
    Object.keys(waitlistFormHandlers).forEach(mode => {
      this.setFormHandler(mode, this[waitlistFormHandlers[mode]]);
    });
  }

  _checkPhoneNumber = (phoneNumber) => {
    const regex = /^[6-9]\d{9}$/;
    return regex.test(phoneNumber);
  };

  _checkEmail = (email) => {
    // eslint-disable-next-line max-len
    const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailPattern.test(email) && !email.includes('*');
  };

  _validateInput = (value, type) => {
    switch (type) {
      case 'email':
        return this._checkEmail(value);
      case 'tel':
        return this._checkPhoneNumber(value);
      default:
        return isPresent(value);
    }
  };

  _updateEmail = async (email) => {
    try {
      await apiRequest(
        'POST',
        '/user/update-email',
        { email },
      );
      trackSubmitStatus(
        'waitlist-account-email-update-form',
        true,
      );
    } catch (error) {
      let message = defaultErrorMessage;
      if (error && error.isFromServer) {
        switch (error.response.status) {
          case 409:
            message = 'Email is already taken';
            break;
          default:
            message = error.responseJson?.message || defaultErrorMessage;
        }
      }

      trackSubmitStatus(
        'waitlist-account-email-update-form',
        false,
        message,
      );
    }
  };

  _handleOtpSubmissionError = (error, form, endpoint) => {
    let message = defaultErrorMessage;
    if (error && error.isFromServer) {
      if (endpoint === '/users/v2/verify') {
        switch (error.response.status) {
          case 409:
            message = 'Phone number already taken';
            break;
          case 401:
            message = `OTP is ${error.responseJson?.result || 'invalid'},`
              + ' please try again';
            break;
          case 403:
            if (error.responseJson?.signature) {
              message = 'Session Limit Exceeded!';
              window.location = `${window.location.origin}`
              + '/users/session-management/?signature='
              + `${error.responseJson?.signature}`
              + '&source_path='
              // eslint-disable-next-line camelcase
              + `${error.responseJson?.source_path}`;
            }
            break;
          default:
            message = error.responseJson?.message || defaultErrorMessage;
        }
      }

      if (endpoint === '/users/v2/account/verify') {
        switch (error.response.status) {
          case 403:
            message = `OTP is ${error.responseJson?.result || 'invalid'},`
              + ' please try again';
            break;
          default:
            message = error.responseJson?.message || defaultErrorMessage;
        }
      }
    }
    form.setFieldError('otp-verification', message);
    trackSubmitStatus(
      'waitlist-login-otp-form',
      false,
      message,
    );
  }

  /* Form handlers */

  _waitlistRegisterOtpHandler = (el) => {
    const form = new Form(el, {
      method: 'POST',
      endpoint: '/users/v2/verify',
      createPayload: (values) => ({
        user: values,
      }),
    });

    form.on('submitted', () => {
      trackSubmitStatus('register-otp-form', true);
      Modal.close(this._id);
      welcomeModalEvents.emit('show');
      setTimeout(() => {
        this.complete();
      }, this._waitTime);
    });

    form.on('error', error => {
      let message = defaultErrorMessage;
      if (error && error.isFromServer) {
        switch (error.response.status) {
          case 404:
            message = 'User does not exist!';
            break;
          case 401:
            message = 'OTP you entered is incorrect';
            break;
          default:
          // Do nothing
        }
        trackSubmitStatus('register-otp-form', false, message);
      }
      form.setError(message);
    });

    form.initialize();

    return form;
  }

  _waitlistLoginMobileHandler = (el) => {
    const selectId = 'waitlist-form__phone-input';
    const endpoint = this._loggedIn ? '/users/v2/account/' : '/users/v2/';
    const captchaAction = this._loggedIn ? 'LOGIN_AND_SIGNUP' : 'SIGN_UP';
    const form = new Form(el, {
      method: this._loggedIn ? 'PUT' : 'POST',
      endpoint,
      createPayload: ({
        email,
        phone_number: phoneNumber,
        'cf-turnstile-response': turnstileResponse,
        whatsapp_consent: consent,
      }) => {
        const countryCode = document.querySelector(`
          #${selectId}-custom-select .selected-dial-code
        `)?.textContent || '+91';
        return {
          user: {
            email,
            phone_number: `${countryCode}-${phoneNumber}`,
            whatsapp_consent: consent,
            country_code: countryCode,
            skip_existing_user_check: true,
            terms_consent: 'true',
          },
          'cf-turnstile-response': turnstileResponse,
          recaptcha_action: captchaAction,
          type: 'school_of_tech',
          attributions: {
            platform: getPlatform(),
          },
        };
      },
    });

    form.on('initialize', () => {
      const whatsappConsentInputEl = document.querySelector(
        '.waitlist-form__wp-consent-checkbox',
      );
      if (whatsappConsentInputEl) {
        whatsappConsentInputEl.addEventListener('click', () => {
          if (whatsappConsentInputEl.value === 'whatsapp_consent_yes') {
            whatsappConsentInputEl.setAttribute('value', 'whatsapp_consent_no');
          } else {
            whatsappConsentInputEl.setAttribute(
              'value', 'whatsapp_consent_yes',
            );
          }
          this._whatsappConsent = whatsappConsentInputEl.value;
        });
      }
      const getOtpBtn = document.querySelector('.waitlist-form__otp-btn');
      if (getOtpBtn) {
        const phoneInputContainer = document.querySelector(
          '.waitlist-form__phone-input',
        );
        const phoneInput = phoneInputContainer?.querySelector('input');
        const emailInputContainer = document.querySelector(
          '.waitlist-form__email-input',
        );
        const emailInput = emailInputContainer?.querySelector('input');

        const enableGetOtpButton = () => {
          if (this._checkEmail(emailInput?.value)
              && this._checkPhoneNumber(phoneInput?.value)) {
            getOtpBtn.disabled = false;
            getOtpBtn.classList.add('waitlist-form__otp-btn--enabled');
          } else {
            getOtpBtn.disabled = true;
            getOtpBtn.classList.remove('waitlist-form__otp-btn--enabled');
          }
        };

        enableGetOtpButton();

        phoneInput?.addEventListener('input', () => {
          phoneInput.value = phoneInput.value.replace(/[^0-9]/g, '');
          if (this._checkPhoneNumber(phoneInput?.value)) {
            const errorInput = phoneInput?.querySelector(
              '.form-field__error',
            );
            errorInput?.classList.add('form-field__error--hidden');
          } else if (phoneInput?.value) {
            form.setFieldError('phone_number', invalidPhoneErrorMessage);
          }

          enableGetOtpButton();
        });

        emailInput?.addEventListener('input', () => {
          if (this._checkEmail(emailInput?.value)) {
            const errorInput = emailInput?.querySelector(
              '.form-field__error',
            );
            errorInput?.classList.add('form-field__error--hidden');
          } else if (emailInput?.value) {
            form.setFieldError('email', errorTypeMessage.email);
          }

          enableGetOtpButton();
        });
      }

      initializeCustomSelect(selectId);
    });

    form.on('submitted', (json) => {
      if (json?.flashError) {
        handleFlashError(json.flashError, 'waitlist-login-mobile-form');
      } else {
        trackSubmitStatus('waitlist-login-mobile-form', true);
        const email = form.getFieldValue('email');
        const phoneNumber = form.getFieldValue('phone_number');
        const countryCode = document
          .querySelector(`
            #${selectId}-custom-select .selected-dial-code
          `)?.textContent || '+91';
        const otpFormMode = 'waitlist-login-otp-autofill';

        const loginOtpForm = this.getForm(otpFormMode);
        const loginOtpPhoneNumber = document.querySelector(
          '.waitlist-form__otp-phone-number',
        );
        loginOtpForm.updateField('email', email);
        loginOtpForm.updateField(
          'phone_number', `${countryCode}-${phoneNumber}`,
        );
        if (loginOtpPhoneNumber) {
          loginOtpPhoneNumber.innerText = `${countryCode}-${phoneNumber}`;
        }
        this.switchMode(otpFormMode);

        // Auto-Fill OTP Feature
        SingularOTPAutofill('waitlist-form_otp_input');
        handleResendOTP();
      }
    });

    form.on('error', (error) => {
      let message = defaultErrorMessage;
      if (error && error.isFromServer) {
        if (endpoint === '/users/v2/') {
          switch (error.response.status) {
            case 422:
              message = 'Please fill the required fields';
              break;
            case 406:
              message = 'Please wait for captcha verification, and try again';
              window.fallbackVisibleTurnstile(el.id);
              break;
            case 400:
              message = error.responseJson?.message || defaultErrorMessage;
              break;
            case 429:
              message = 'Requested too many OTPs, please try after sometime';
              break;
            default:
              message = error.responseJson?.message || defaultErrorMessage;
          }
        }

        if (endpoint === '/users/v2/account/') {
          switch (error.response.status) {
            case 422:
              message = error.responseJson?.message;
              window.fallbackVisibleTurnstile(el.id);
              break;
            case 409:
              message = 'Phone number already registered';
              break;
            case 429:
              message = 'Requested too many OTPs, please try after sometime';
              break;
            default:
              message = error.responseJson?.message || defaultErrorMessage;
          }
        }

        trackSubmitStatus(
          'waitlist-login-mobile-form',
          false,
          message,
        );
      }

      form.setError(message);
    });

    form.initialize();

    return form;
  }

  _waitlistLoginOtpHandler = (el) => {
    const endpoint = this._loggedIn
      ? '/users/v2/account/verify'
      : '/users/v2/verify';
    const form = new Form(el, {
      method: 'POST',
      endpoint,
      createPayload: ({
        email,
        phone_number: phoneNumber,
        otp_1: otpOne = '',
        otp_2: otpTwo = '',
        otp_3: otpThree = '',
        otp_4: otpFour = '',
        otp_5: otpFive = '',
        otp_6: otpSix = '',
      }) => ({
        user: {
          email,
          phone_number: phoneNumber,
          otp: `${otpOne}${otpTwo}${otpThree}${otpFour}${otpFive}${otpSix}`,
          skip_existing_user_check: true,
        },
        type: 'school_of_tech',
        attributions: {
          intent: 'otp_verified',
          product: 'scaler',
          sub_product: 'undergrad',
          element: 'otp_modal',
          program: 'school_of_tech',
        },
      }),
    });

    form.on('submitted', (json) => {
      if (json?.flashError) {
        handleFlashError(json.flashError, 'waitlist-login-otp-form');
      } else {
        postPhoneVerificationActions(json);
      }
    });

    form.on('error', error => {
      this._handleOtpSubmissionError(error, form, endpoint);
      const inputs = document.querySelectorAll(
        '.waitlist-form__otp-input',
      );
      inputs.forEach(input => {
        input.classList.add('waitlist-form__otp-input--error');
      });
    });

    form.on('initialize', () => {
      const inputs = document.querySelectorAll(
        '.waitlist-form__otp-input',
      );
      const proceedbtn = document.querySelector(
        '.waitlist-form__proceed-otp-btn',
      );
      proceedbtn.disabled = true;
      const resetOtpInputs = () => {
        inputs.forEach(input => {
          input.classList.remove('waitlist-form__otp-input--error');
        });

        const otpInputs = document.querySelector(
          '.waitlist-form__otp',
        );
        const errorInput = otpInputs?.querySelector(
          '.form-field__error',
        );
        if (errorInput) {
          errorInput.innerHTML = '';
          errorInput.classList.add('form-field__error--hidden');
        }
      };
      const enabledProceedBtn = () => {
        const otpFilled = Array.from(inputs).every(
          ele => isPresent(ele.value),
        );

        if (otpFilled) {
          proceedbtn.disabled = false;
          proceedbtn?.classList.add(
            'waitlist-form__proceed-otp-btn--active',
          );
        } else {
          proceedbtn.disabled = true;
          proceedbtn?.classList.remove(
            'waitlist-form__proceed-otp-btn--active',
          );
        }
      };
      const phoneNumberEdit = document.querySelector(
        '.waitlist-form__phone-otp-edit',
      );
      phoneNumberEdit?.addEventListener('click', () => {
        resetOtpInputs();
      });
      const totalOtpInputs = inputs.length;
      inputs.forEach((input, index) => {
        ['focus', 'blur'].forEach(listener => {
          input.addEventListener(listener, () => {
            if (isPresent(input.value)) {
              inputs[index].classList.add('waitlist-form__otp--filled');
            } else {
              inputs[index].classList.add('waitlist-form__otp--filled');
            }
            enabledProceedBtn();
            resetOtpInputs();
          });
        });

        // eslint-disable-next-line consistent-return
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Backspace') {
            if (isPresent(inputs[index].value)) {
              inputs[index].value = '';
            } else if (index !== 0) {
              inputs[index - 1].focus();
            }
          }
          enabledProceedBtn();
          resetOtpInputs();
        });

        input.addEventListener('input', () => {
          inputs[index].value = inputs[index].value.replace(/[^0-9]/g, '');
          if (inputs[index].value !== '') {
            if (index !== totalOtpInputs - 1) inputs[index + 1].focus();
          }
          enabledProceedBtn();
          resetOtpInputs();
        });
      });

      document.addEventListener('waitlist-login-otp-form', () => {
        handleResendOTP();
      });

      handleExitIntentCloseBtn();
      handleResendOtpEls('waitlist-exit-intent__resend-otp-channel');
    });

    form.initialize();

    return form;
  }

  _waitlistLoginOtpAutofillHandler = (el) => {
    const endpoint = this._loggedIn
      ? '/users/v2/account/verify'
      : '/users/v2/verify';
    const form = new Form(el, {
      method: 'POST',
      endpoint,
      createPayload: ({
        email,
        otp,
        phone_number: phoneNumber,
      }) => ({
        user: {
          email,
          otp,
          phone_number: phoneNumber,
          skip_existing_user_check: true,
        },
        type: 'school_of_tech',
        attributions: {
          intent: 'otp_verified',
          product: 'scaler',
          sub_product: 'undergrad',
          element: 'otp_modal',
          program: 'school_of_tech',
        },
      }),
    });

    form.on('submitted', (json) => {
      if (json?.flashError) {
        handleFlashError(json.flashError, 'waitlist-login-otp-form');
      } else {
        postPhoneVerificationActions(json);
      }
    });

    form.on('error', error => {
      this._handleOtpSubmissionError(error, form, endpoint);
      const input = document.querySelector(
        '.waitlist-form__otp-single-input',
      );
      input.classList.add('waitlist-form__otp-single-input--error');
    });

    form.on('initialize', () => {
      const input = document.querySelector(
        '.waitlist-form__otp-single-input',
      );
      const proceedbtn = document.querySelector(
        '.waitlist-form__proceed-otp-btn',
      );
      proceedbtn?.setAttribute('disabled', true);
      const resetOtpInput = () => {
        input?.classList.remove('waitlist-form__otp-single-input--error');

        const otpInput = document.querySelector(
          '.waitlist-form__otp',
        );
        const errorInput = otpInput?.querySelector(
          '.form-field__error',
        );
        if (errorInput) {
          errorInput.innerHTML = '';
          errorInput.classList.add('form-field__error--hidden');
        }
      };
      const enabledProceedBtn = () => {
        const otpFilled = isPresent(input.value) && input.value.length === 6;

        if (otpFilled) {
          proceedbtn.disabled = false;
          proceedbtn?.classList.add(
            'waitlist-form__proceed-otp-btn--active',
          );
        } else {
          proceedbtn.disabled = true;
          proceedbtn?.classList.remove(
            'waitlist-form__proceed-otp-btn--active',
          );
        }
      };
      const phoneNumberEdit = document.querySelector(
        '.waitlist-form__phone-otp-edit',
      );
      phoneNumberEdit?.addEventListener('click', () => {
        resetOtpInput();
      });
      ['focus', 'blur'].forEach(listener => {
        input.addEventListener(listener, () => {
          input.classList.add('waitlist-form__otp--filled');
          enabledProceedBtn();
          resetOtpInput();
        });
      });

      input.addEventListener('input', () => {
        input.value = input.value.replace(/[^0-9]/g, '');
        enabledProceedBtn();
        resetOtpInput();
      });

      form.element.addEventListener('singular-otp-autofilled', () => {
        form.submit();
      });

      document.addEventListener('waitlist-login-otp-form', () => {
        handleResendOTP();
      });

      handleExitIntentCloseBtn();
      handleResendOtpEls('waitlist-exit-intent__resend-otp-channel');
    });

    form.initialize();

    return form;
  }

  _waitlistAccountUpdateHandler = (el) => {
    const form = new Form(el, {
      method: 'POST',
      endpoint: '/user/update-whatsapp-notification-settings',
      createPayload: ({ whatsapp_consent: consent }) => ({
        whatsapp_consent: consent,
      }),
    });

    form.on('initialize', () => {
      const whatsappConsentInputEl = document.querySelector(
        '.waitlist-form__wp-consent-update-checkbox',
      );
      if (whatsappConsentInputEl) {
        whatsappConsentInputEl.addEventListener('click', () => {
          if (whatsappConsentInputEl.value === 'whatsapp_consent_yes') {
            whatsappConsentInputEl.setAttribute('value', 'whatsapp_consent_no');
          } else {
            whatsappConsentInputEl.setAttribute(
              'value', 'whatsapp_consent_yes',
            );
          }
          this._whatsappConsent = whatsappConsentInputEl.value;
        });
      }
      const proceedBtn = document.querySelector('.waitlist-form__update-btn');
      if (proceedBtn) {
        const emailInputContainer = document.querySelector(
          '.waitlist-form__email-update-input',
        );
        const emailInput = emailInputContainer?.querySelector('input');
        const currentEmail = emailInput?.value;

        const enableUpdateButton = () => {
          if (this._checkEmail(emailInput?.value)) {
            proceedBtn.disabled = false;
            proceedBtn.classList.add('waitlist-form__update-btn--enabled');
          } else {
            proceedBtn.disabled = true;
            proceedBtn.classList.remove('waitlist-form__update-btn--enabled');
          }
        };

        enableUpdateButton();

        emailInput?.addEventListener('input', () => {
          if (this._checkEmail(emailInput?.value)) {
            const errorInput = emailInput?.querySelector(
              '.form-field__error',
            );
            errorInput?.classList.add('form-field__error--hidden');
          } else if (emailInput?.value) {
            form.setFieldError('email', errorTypeMessage.email);
          }

          enableUpdateButton();
        });

        proceedBtn.addEventListener('click', async () => {
          const updatedEmail = emailInput?.value;
          if (currentEmail !== updatedEmail) {
            await this._updateEmail(updatedEmail);
          }
        });
      }
    });

    form.on('submitted', () => {
      trackSubmitStatus('waitlist-account-update-form', true);
      this.switchMode('waitlist-form');
    });

    form.on('error', (error) => {
      const message = error.responseJson?.message || defaultErrorMessage;
      trackSubmitStatus(
        'waitlist-account-update-form',
        false,
        message,
      );

      form.setError(message);
    });

    form.initialize();

    return form;
  }

  _waitlistFormHandler = (el) => {
    const studentFormGroup = el.querySelector('#waitlist-form-student');
    const parentFormGroup = el.querySelector('#waitlist-form-parent');
    const formGroups = [
      studentFormGroup,
      parentFormGroup,
    ];
    const waitlistRadioToggleGroups = el.querySelectorAll(
      '.waitlist-form__radio-btn',
    );
    let activeRole = 'student';
    const roleConfig = {
      student: {
        gradYearSelect: new Select(
          `${studentFormGroup.id}-gradyear`,
          { searchable: true },
        ),
        formGroup: studentFormGroup,
        formGroupLabel: studentFormGroup.getAttribute('data-form-label'),
      },
      parent: {
        gradYearSelect: new Select(
          `${parentFormGroup.id}-gradyear`,
          { searchable: true },
        ),
        formGroup: parentFormGroup,
        formGroupLabel: parentFormGroup.getAttribute('data-form-label'),
      },
    };

    const payloadResponses = (values) => {
      const {
        gradYearSelect,
        formGroup,
        formGroupLabel,
      } = roleConfig[activeRole];
      const payload = {
        slug: 'school_of_tech',
        form_group_label: formGroupLabel,
      };
      const formResponses = {};
      const gradYearSelectId = gradYearSelect?._hiddenInputEl.name;
      formResponses[gradYearSelectId] = values[gradYearSelectId];
      const inputWrappers = formGroup.querySelectorAll(
        '.waitlist-form__join-input-wrapper',
      );
      Array.from(inputWrappers).forEach(inputWrapper => {
        const formInputEl = inputWrapper.querySelector('.form-input');

        formResponses[formInputEl.name] = values[formInputEl.name];
      });
      waitlistRadioToggleGroups.forEach(radioGroup => {
        const input = radioGroup.querySelector('input');
        if (input.checked) {
          formResponses[input.name] = values[input.name];
        }
      });
      payload.form_responses = formResponses;

      return payload;
    };

    const form = new Form(el, {
      method: 'PUT',
      endpoint: '/api/v3/programs/school_of_tech/forms/respond',
      createPayload: (values) => payloadResponses(values),
    });

    const allFieldsValid = (formGroup, nextBtn) => {
      const proceedBtn = nextBtn;
      let isValid = true;
      const formRole = formGroup.getAttribute('data-visible-for');
      isValid &&= isPresent(roleConfig[formRole].gradYearSelect?.selectedValue);
      const inputWrappers = formGroup.querySelectorAll(
        '.waitlist-form__join-input-wrapper',
      );
      Array.from(inputWrappers).forEach(inputWrapper => {
        const formInputEl = inputWrapper.querySelector('.form-input');
        if (formInputEl) {
          const { value, type } = formInputEl;
          isValid &&= this._validateInput(value, type);
        }
      });
      if (isValid) {
        proceedBtn.disabled = false;
        proceedBtn.classList.add('waitlist-form__proceed-btn--active');
      } else {
        proceedBtn.disabled = true;
        proceedBtn.classList.remove('waitlist-form__proceed-btn--active');
      }
    };

    const toggleFormGroupDisabledClick = () => {
      const disablePointerEventsClass = 'waitlist-form__disable-pointer';
      formGroups.forEach(group => {
        if (group.classList.contains(disablePointerEventsClass)) {
          group.classList.add(disablePointerEventsClass);
        } else {
          group.classList.remove(disablePointerEventsClass);
        }
      });
    };

    form.on('initialize', () => {
      // update the data attribute in parent form based on the params
      WaitlistModalActions.updateParamsOnPageView();
      handleQueryParams(el);
      const proceedBtn = document.querySelector('.waitlist-form__proceed-btn');
      proceedBtn.disabled = true;
      proceedBtn.classList.remove('waitlist-form__proceed-btn--active');
      proceedBtn.addEventListener('click', toggleFormGroupDisabledClick);
      const radioBtnClickHandler = (radioBtn) => {
        waitlistRadioToggleGroups.forEach(btn => {
          const input = btn.querySelector('input');
          btn.classList.remove('waitlist-form__radio-btn--checked');
          if (input) {
            input.checked = false;
          }
        });
        formGroups.forEach(group => group.classList.add('hidden'));
        const input = radioBtn.querySelector('input');
        radioBtn.classList.add('waitlist-form__radio-btn--checked');
        if (input) {
          input.checked = true;
          activeRole = input.getAttribute('data-user-role');
        }
        const targetFormGroupId = input.getAttribute('data-form-target');
        const targetFormGroup = el.querySelector(
          `#${targetFormGroupId}`,
        );
        targetFormGroup?.classList.remove('hidden');
        proceedBtn.disabled = true;
        proceedBtn.classList.remove('waitlist-form__proceed-btn--active');
        allFieldsValid(targetFormGroup, proceedBtn);
      };
      waitlistRadioToggleGroups.forEach(radioBtn => {
        radioBtn.addEventListener('click', () => {
          radioBtnClickHandler(radioBtn);
        });
      });
      if (waitlistRadioToggleGroups.length > 0) {
        const radioBtn = waitlistRadioToggleGroups[0];
        radioBtnClickHandler(radioBtn);
      }

      const setupFormValidations = (formGroup) => {
        const inputWrappers = formGroup.querySelectorAll(
          '.waitlist-form__join-input-wrapper',
        );
        Array.from(inputWrappers).forEach(inputWrapper => {
          const formInputEl = inputWrapper.querySelector('.form-input');
          const errorEl = inputWrapper.querySelector('.form-field__error');
          if (formInputEl) {
            formInputEl.addEventListener('input', (event) => {
              const { value, type } = event.target;
              if (type === 'tel') {
                formInputEl.value = formInputEl.value.replace(/[^0-9]/g, '');
              }
              const isValid = this._validateInput(value, type);

              if (!isValid) {
                showError(errorEl, formInputEl);
                errorEl.innerHTML = errorTypeMessage[type];
              } else {
                hideError(errorEl, formInputEl);
              }
              allFieldsValid(formGroup, proceedBtn);
            });
          }
        });
        const formRole = formGroup.getAttribute('data-visible-for');
        const {
          gradYearSelect,
        } = roleConfig[formRole];
        ValidateSelect.initialize({
          element: gradYearSelect,
          formId: el.id,
        });
        gradYearSelect.on('change', () => {
          allFieldsValid(formGroup, proceedBtn);
        });
      };

      formGroups.forEach(formGroup => {
        setupFormValidations(formGroup);
      });
    });

    form.on('submitted', (json) => {
      const {
        formGroupLabel: formTrackingId,
        gradYearSelect: gradSelect,
      } = roleConfig[activeRole];

      if (json.success) {
        // cases based on the attribute value in the parent form element
        // which is directly modified by either clicks on the elements
        // or by the params we got from the previous steps
        const { email: userEmail } = json.data;
        if (window.GTMData?.email !== userEmail) {
          window.GTMtracker?.trackEvent('logoutFormatter');
          gtmTracking.sendTrackUser(userEmail);
        }

        Modal.close('waitlist-modal');

        const gradYear = gradSelect.selectedValue;
        const dataSuccessAction = parentFormEl.getAttribute(
          'data-success-action',
        );
        const dataSuccessView = parentFormEl.getAttribute(
          'data-success-view',
        );

        const params = dataSuccessView
          ? `${formTrackingId}-${dataSuccessView}`
          : formTrackingId;

        switch (dataSuccessAction) {
          case 'application_process':
            this._commonFlow(gradYear, params);
            this._applicationProcessFlow();
            break;
          case 'nset_preparation_video_button':
            this._commonFlow(gradYear,
              `${formTrackingId}-nset-preparation-video-button`);
            this._nsetPreparationFlow();
            break;
          case 'nset_preparation_video_thumbnail':
            this._commonFlow(gradYear,
              `${formTrackingId}-nset-preparation-video-thumbnail`);
            this._nsetPreparationFlow();
            break;
          case 'download_brochure':
            this._commonFlow(gradYear, `${formTrackingId}-download_brochure`);
            this._downloadBrochureFlow();
            break;
          case 'internship_report_download':
            this._commonFlow(
              gradYear,
              `${formTrackingId}-download_internship_report`,
            );
            this._downloadInternshipReportFlow();
            break;
          case 'placement_download':
            this._downloadPlacementFlow();
            break;
          case 'download_curriculum':
            this._commonFlow(gradYear, `${formTrackingId}-download_curriculum`);
            this._downloadCurriculumFlow();
            break;
          case 'download_newsletter':
            this._commonFlow(gradYear, `${formTrackingId}-download_newsletter`);
            this._downloadNewsletterFlow();
            break;
          case 'book_counselling':
            this._commonFlow(gradYear, `${formTrackingId}-book_counselling`);
            this._bookCounsellingFlow(gradYear);
            break;
          case 'prepkit_download':
            this._commonFlow(gradYear, `${formTrackingId}-prepkit_download`);
            this._prepkitDownloadFlow();
            break;
          case 'call_us_directly':
            this._commonFlow(gradYear, `${formTrackingId}-call_us_directly`);
            this._callUsDirectlyFlow();
            break;
          default:
            break;
        }

        if (dataSuccessAction !== 'application_process') {
          const url = new URL(window.location.href);
          const urlParams = new URLSearchParams(url.search);
          urlParams.delete('open-waitlist-form');
          url.search = urlParams.toString();
          window.location.href = url.toString();
        }
      } else {
        form.setFieldError('otp-verification', json.message);
        const inputs = document.querySelectorAll(
          '.waitlist-form__otp-input',
        );
        inputs.forEach(input => {
          input.classList.add('waitlist-form__otp-input--error');
        });
        trackSubmitStatus(
          `${formTrackingId}`,
          false,
          'Server Error',
        );
      }
    });

    form.on('error', error => {
      toggleFormGroupDisabledClick();
      const formTrackingId = roleConfig[activeRole].formGroupLabel;

      const message = defaultErrorMessage;
      if (error && error.isFromServer) {
        switch (error.response.status) {
          default:
          // Do nothing
        }
      }
      form.setError(message);
      trackSubmitStatus(
        `${formTrackingId}`,
        false,
        'Server Error',
      );
    });

    form.initialize();

    return form;
  };
}

export default WaitlistFlow;
